import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { cn } from '#app/utils/misc.tsx'
import { Icon } from './icon.tsx'
import { Label } from './label.tsx'
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip.tsx'
import { type IconName } from '@/icon-name'

export interface InputTextProps
	extends Omit<React.ComponentProps<'input'>, 'size'>,
		VariantProps<typeof inputTextVariants> {
	/**
	 * Label del campo de entrada
	 */
	label?: string
	/**
	 * Posición del label (arriba o interno)
	 */
	labelPosition?: 'up' | 'internal'
	/**
	 * Texto de soporte debajo del campo
	 */
	supportingText?: string
	/**
	 * Texto de error debajo del campo
	 */
	errorText?: string
	/**
	 * Texto del tooltip
	 */
	tooltipText?: string
	/**
	 * Indica si el campo es requerido
	 */
	required?: boolean

	// Nueva API - Figma Aligned Props
	/**
	 * Mostrar icono en el lado izquierdo (Figma: show-IconL)
	 */
	showIconL?: boolean
	/**
	 * Icono del lado izquierdo
	 */
	iconL?: IconName
	/**
	 * Mostrar icono en el lado derecho (Figma: show-IconR)
	 */
	showIconR?: boolean
	/**
	 * Icono del lado derecho
	 */
	iconR?: IconName
	/**
	 * Mostrar prefijo (Figma: Prefix boolean)
	 */
	showPrefix?: boolean
	/**
	 * Texto del prefijo (Figma: Prefix Text)
	 */
	prefixText?: string
	/**
	 * Mostrar sufijo (Figma: Sufix boolean)
	 */
	showSuffix?: boolean
	/**
	 * Texto del sufijo (Figma: Sufix text)
	 */
	suffixText?: string
	/**
	 * Indica si mostrar flecha de dropdown
	 */
	showDropdown?: boolean
	/**
	 * Estado del campo (incluyendo nuevos estados de Figma)
	 */
	state?: 'default' | 'hover' | 'focused' | 'error' | 'success' | 'completed'
	/**
	 * Tamaño del campo
	 */
	size?: 's' | 'm' | 'l'
	/**
	 * Manejador de click para el contenedor del input
	 */
	onClick?: (event: React.MouseEvent<HTMLDivElement>) => void

	// Deprecated props for backward compatibility
	/**
	 * @deprecated Use showIconL and iconL instead
	 */
	icon?: IconName
	/**
	 * @deprecated Use showIconL/showIconR instead
	 */
	iconPosition?: 'left' | 'right'
	/**
	 * @deprecated Use prefixText instead
	 */
	prefix?: string
	/**
	 * @deprecated Use suffixText instead
	 */
	suffix?: string
}

// Custom hook for input state management
const useInputState = (
	initialValue: string = '',
	state: InputTextProps['state'] = 'default',
	errorText?: string,
	currentValue?: string,
) => {
	const [isFocused, setIsFocused] = React.useState(false)
	const [isHovered, setIsHovered] = React.useState(false)
	const [hasValue, setHasValue] = React.useState(initialValue.length > 0)

	// Sync hasValue with current prop value
	React.useEffect(() => {
		const valueToCheck = currentValue || initialValue
		setHasValue(valueToCheck.length > 0)
	}, [currentValue, initialValue])

	// Memoized current state calculation
	const currentState = React.useMemo(() => {
		if (errorText) return 'error'
		if (state === 'focused' || isFocused) return 'focused'
		if (state === 'hover' || isHovered) return 'hover'
		if (state === 'success' || state === 'completed') return state
		if (hasValue && state === 'default') return 'completed'
		return 'default'
	}, [errorText, state, isFocused, isHovered, hasValue])

	// Memoized event handlers
	const handleFocus = React.useCallback(
		(
			e: React.FocusEvent<HTMLInputElement>,
			onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void,
		) => {
			setIsFocused(true)
			onFocus?.(e)
		},
		[],
	)

	const handleBlur = React.useCallback(
		(
			e: React.FocusEvent<HTMLInputElement>,
			onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void,
		) => {
			setIsFocused(false)
			onBlur?.(e)
		},
		[],
	)

	const handleChange = React.useCallback(
		(
			e: React.ChangeEvent<HTMLInputElement>,
			onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void,
		) => {
			setHasValue(e.target.value.length > 0)
			onChange?.(e)
		},
		[],
	)

	const handleMouseEnter = React.useCallback(() => {
		setIsHovered(true)
	}, [])

	const handleMouseLeave = React.useCallback(() => {
		setIsHovered(false)
	}, [])

	return {
		currentState,
		isFocused,
		hasValue,
		handleFocus,
		handleBlur,
		handleChange,
		handleMouseEnter,
		handleMouseLeave,
	}
}

const inputTextVariants = cva(
	'relative flex size-full content-stretch items-center justify-start rounded-[12px] bg-white',
	{
		variants: {
			size: {
				s: 'h-9 max-h-[36px] min-h-[36px]',
				m: 'h-10 max-h-[40px] min-h-[40px]',
				l: 'h-[52px] max-h-[52px] min-h-[52px]',
			},
			labelPosition: {
				up: '',
				internal: '',
			},
			state: {
				default: '',
				hover: '',
				focused: '',
				error: '',
				success: '',
				completed: '',
			},
			showIconL: {
				true: '',
				false: '',
			},
			showIconR: {
				true: '',
				false: '',
			},
			showDropdown: {
				true: '',
				false: '',
			},
			showPrefix: {
				true: '',
				false: '',
			},
			showSuffix: {
				true: '',
				false: '',
			},
		},
		compoundVariants: [
			// Estados específicos - usar design tokens
			{
				state: 'error',
				className: 'border-[var(--input-border-error)]',
			},
			{
				state: 'default',
				className: 'border-[var(--input-border-default)]',
			},
			{
				state: 'focused',
				className:
					'border-[var(--input-border-focused)] shadow-[var(--input-focus-shadow)]',
			},
			{
				state: 'hover',
				className:
					'border-[var(--input-border-hover)] shadow-[var(--input-hover-shadow)]',
			},
			{
				state: 'success',
				className: 'border-[var(--input-border-success)]',
			},
			{
				state: 'completed',
				className: 'border-[var(--input-border-completed)]',
			},
		],
		defaultVariants: {
			size: 'm',
			labelPosition: 'up',
			state: 'default',
			showIconL: false,
			showIconR: false,
			showDropdown: false,
			showPrefix: false,
			showSuffix: false,
		},
	},
)

const labelVariants = cva(
	"font-['Parkinsans:Regular',_sans-serif] leading-[0] not-italic",
	{
		variants: {
			position: {
				up: 'text-[14px] leading-[22px] text-[var(--input-label)]',
				internal: 'text-[16px] leading-[30px] text-[var(--input-text)]',
			},
			required: {
				true: 'after:ml-1 after:w-2 after:text-center after:text-[14px] after:leading-[22px] after:text-[var(--input-required)] after:content-["*"]',
				false: '',
			},
		},
		defaultVariants: {
			position: 'up',
			required: false,
		},
	},
)

const tooltipIconVariants = cva(
	'relative flex shrink-0 items-center justify-center',
	{
		variants: {
			size: {
				s: 'h-4 w-4',
				m: 'h-4 w-4',
				l: 'h-5 w-5',
			},
		},
		defaultVariants: {
			size: 'm',
		},
	},
)

const InputText = React.forwardRef<HTMLDivElement, InputTextProps>(
	(
		{
			className,
			label,
			labelPosition = 'up',
			supportingText,
			errorText,
			tooltipText,
			required = false,

			// New Figma-aligned props
			showIconL = false,
			iconL,
			showIconR = false,
			iconR,
			showPrefix = false,
			prefixText,
			showSuffix = false,
			suffixText,
			showDropdown = false,
			onClick,

			// Deprecated props (backward compatibility)
			prefix,
			suffix,
			icon,
			iconPosition = 'left',

			// Component state
			state = 'default',
			size = 'm',
			id,
			placeholder,
			...props
		},
		ref,
	) => {
		const inputId = React.useId()
		const finalId = id || inputId

		// Use optimized state management hook
		const {
			currentState,
			isFocused,
			hasValue,
			handleFocus: optimizedHandleFocus,
			handleBlur: optimizedHandleBlur,
			handleChange: optimizedHandleChange,
			handleMouseEnter,
			handleMouseLeave,
		} = useInputState(
			props.value?.toString() || props.defaultValue?.toString() || '',
			state,
			errorText,
			props.value?.toString(), // Pass current value for synchronization
		)

		// Memoized backward compatibility logic
		const compatibility = React.useMemo(
			() => ({
				resolvedShowIconL: showIconL || (icon && iconPosition === 'left'),
				resolvedIconL: iconL || (iconPosition === 'left' ? icon : undefined),
				resolvedShowIconR: showIconR || (icon && iconPosition === 'right'),
				resolvedIconR: iconR || (iconPosition === 'right' ? icon : undefined),
				resolvedShowPrefix: showPrefix || !!prefix,
				resolvedPrefixText: prefixText || prefix,
				resolvedShowSuffix: showSuffix || !!suffix,
				resolvedSuffixText: suffixText || suffix,
			}),
			[
				showIconL,
				showIconR,
				iconL,
				iconR,
				showPrefix,
				showSuffix,
				prefixText,
				suffixText,
				icon,
				iconPosition,
				prefix,
				suffix,
			],
		)

		// Optimized event handlers with proper callback forwarding
		const handleFocus = React.useCallback(
			(e: React.FocusEvent<HTMLInputElement>) => {
				optimizedHandleFocus(e, props.onFocus)
			},
			[optimizedHandleFocus, props.onFocus],
		)

		const handleBlur = React.useCallback(
			(e: React.FocusEvent<HTMLInputElement>) => {
				optimizedHandleBlur(e, props.onBlur)
			},
			[optimizedHandleBlur, props.onBlur],
		)

		const handleChange = React.useCallback(
			(e: React.ChangeEvent<HTMLInputElement>) => {
				optimizedHandleChange(e, props.onChange)
			},
			[optimizedHandleChange, props.onChange],
		)

		return (
			<div className="inline-flex w-full flex-col items-start justify-start gap-1">
				{/* Label externo */}
				{label && labelPosition === 'up' && (
					<div className="flex flex-col items-start justify-start gap-1 self-stretch">
						<div className="inline-flex items-center justify-start gap-1">
							<Label
								htmlFor={finalId}
								className={cn(
									labelVariants({ position: 'up', required: false }),
								)}
							>
								{label}
							</Label>
							{/* Asterisco requerido - Elemento #2 */}
							{required && (
								<div className="relative shrink-0 text-center font-['Parkinsans:Regular',_sans-serif] text-[14px] leading-[0] text-nowrap text-[var(--input-required)] not-italic">
									<p className="leading-[22px] whitespace-pre">*</p>
								</div>
							)}
							{tooltipText && (
								<Tooltip>
									<TooltipTrigger asChild>
										<button
											type="button"
											className={cn(tooltipIconVariants({ size }))}
											aria-label="Información adicional"
										>
											<Icon
												name="question-mark-circled"
												size="sm"
												className="text-[var(--input-supporting)]"
											/>
										</button>
									</TooltipTrigger>
									<TooltipContent>{tooltipText}</TooltipContent>
								</Tooltip>
							)}
						</div>
					</div>
				)}

				{/* Contenedor del input */}
				<div className="flex flex-col items-start justify-start gap-1.5 self-stretch">
					<div className="inline-flex items-center justify-start gap-1.5 self-stretch">
						{/* Input principal */}
						<div
							ref={ref}
							data-name={`size=${size.toUpperCase()}, Ubicacion Label=${labelPosition === 'internal' ? 'Interno' : 'Externo'}, Type=regular, State=${currentState.charAt(0).toUpperCase() + currentState.slice(1)}, icons=${compatibility.resolvedShowIconL || compatibility.resolvedShowIconR}, dropdown=${showDropdown}`}
							onMouseEnter={handleMouseEnter}
							onMouseLeave={handleMouseLeave}
							onClick={onClick}
							className={cn(
								'flex w-full items-center justify-start',
								inputTextVariants({
									size,
									labelPosition,
									state: currentState as
										| 'default'
										| 'hover'
										| 'focused'
										| 'error'
										| 'success'
										| 'completed',
									showIconL: compatibility.resolvedShowIconL,
									showIconR: compatibility.resolvedShowIconR,
									showDropdown,
									showPrefix: compatibility.resolvedShowPrefix,
									showSuffix: compatibility.resolvedShowSuffix,
								}),
								className,
							)}
						>
							{/* Contenedor principal del input */}
							<div className="max-h-inherit min-h-inherit relative flex size-full content-stretch items-center justify-start overflow-clip">
								<div className="relative box-border flex h-full min-h-px min-w-px shrink-0 grow basis-0 content-stretch items-center justify-start gap-3 px-4 py-1">
									{/* Icono izquierdo */}
									{compatibility.resolvedShowIconL &&
										compatibility.resolvedIconL && (
											<div className="flex size-4 shrink-0 items-center justify-center">
												<Icon name={compatibility.resolvedIconL} size="sm" />
											</div>
										)}

									{/* Prefijo */}
									{compatibility.resolvedShowPrefix &&
										compatibility.resolvedPrefixText && (
											<span className="text-[16px] leading-[30px] text-[var(--input-text)]">
												{compatibility.resolvedPrefixText}
											</span>
										)}

									{/* Input principal */}
									<div className="relative min-h-px min-w-px shrink-0 grow basis-0 overflow-hidden font-['Parkinsans:Regular',_sans-serif] text-[16px] leading-[0] text-nowrap overflow-ellipsis text-[var(--input-text)] not-italic">
										{labelPosition === 'internal' ? (
											/* Input con label interno flotante */
											<div className="relative flex h-full w-full flex-col justify-center">
												{/* Label flotante */}
												<div
													className={`pointer-events-none absolute transition-all duration-200 ${
														isFocused || hasValue
															? 'top-1 left-0 text-[10px] leading-[12px] text-[var(--input-text)]'
															: 'top-1/2 left-0 -translate-y-1/2 text-[16px] leading-[20px] text-[var(--input-text)]/60'
													}`}
												>
													<div className="inline-flex items-center gap-1">
														<span>{label}</span>
														{required && (
															<span className="text-[12px] text-[var(--input-required)]">
																*
															</span>
														)}
													</div>
												</div>
												{/* Input real */}
												<input
													id={finalId}
													data-slot="input-text"
													className={`w-full border-none bg-transparent outline-none ${
														isFocused || hasValue ? 'pt-5 pb-1' : 'py-3'
													} transition-all duration-200`}
													placeholder={isFocused ? undefined : ''}
													onFocus={handleFocus}
													onBlur={handleBlur}
													onChange={handleChange}
													aria-invalid={!!errorText}
													aria-required={required}
													aria-describedby={
														[
															errorText && `${finalId}-error`,
															supportingText && `${finalId}-supporting`,
														]
															.filter(Boolean)
															.join(' ') || undefined
													}
													{...props}
												/>
											</div>
										) : (
											/* Input normal para label externo */
											<input
												id={finalId}
												data-slot="input-text"
												className="w-full border-none bg-transparent outline-none"
												placeholder={placeholder}
												onFocus={handleFocus}
												onBlur={handleBlur}
												onChange={handleChange}
												aria-invalid={!!errorText}
												aria-required={required}
												aria-describedby={
													[
														errorText && `${finalId}-error`,
														supportingText && `${finalId}-supporting`,
													]
														.filter(Boolean)
														.join(' ') || undefined
												}
												{...props}
											/>
										)}
									</div>

									{/* Sufijo */}
									{compatibility.resolvedShowSuffix &&
										compatibility.resolvedSuffixText && (
											<span className="text-[16px] leading-[30px] text-[var(--input-text)]">
												{compatibility.resolvedSuffixText}
											</span>
										)}

									{/* Icono derecho */}
									{compatibility.resolvedShowIconR &&
										compatibility.resolvedIconR && (
											<div className="flex size-4 shrink-0 items-center justify-center">
												<Icon name={compatibility.resolvedIconR} size="sm" />
											</div>
										)}

									{/* Flecha de dropdown */}
									{showDropdown && (
										<div className="flex size-4 shrink-0 items-center justify-center">
											<Icon name="arrow-down-02-round" size="sm" />
										</div>
									)}
								</div>
							</div>

							{/* Borde del input */}
							<div
								aria-hidden="true"
								className={cn(
									'pointer-events-none absolute inset-0 border border-solid',
									currentState === 'error'
										? 'border-[var(--input-border-error)]'
										: currentState === 'focused'
											? 'border-[var(--input-border-focused)]'
											: currentState === 'hover'
												? 'border-[var(--input-border-hover)]'
												: currentState === 'success'
													? 'border-[var(--input-border-success)]'
													: currentState === 'completed'
														? 'border-[var(--input-border-completed)]'
														: 'border-[var(--input-border-default)]',
									size === 'l'
										? 'rounded-[var(--input-radius-l)]'
										: 'rounded-[var(--input-radius-m)]',
								)}
							/>
						</div>
						{/* Tooltip para label interno */}
						{tooltipText && labelPosition === 'internal' && (
							<Tooltip>
								<TooltipTrigger asChild>
									<button
										type="button"
										className={cn(tooltipIconVariants({ size }))}
										aria-label="Información adicional"
									>
										<Icon
											name="question-mark-circled"
											size="sm"
											className="text-[var(--input-supporting)]"
										/>
									</button>
								</TooltipTrigger>
								<TooltipContent>{tooltipText}</TooltipContent>
							</Tooltip>
						)}
					</div>
				</div>

				{/* Hint Text - Elemento #5 */}
				{supportingText && (
					<div className="inline-flex items-center justify-start gap-2 self-stretch">
						<div
							id={`${finalId}-supporting`}
							className="relative shrink-0 font-['Parkinsans:Regular',_sans-serif] text-[14px] leading-[0] text-nowrap text-[var(--input-supporting)] not-italic"
						>
							<p className="leading-[22px] whitespace-pre">{supportingText}</p>
						</div>
					</div>
				)}

				{/* Error Section - Elementos #6, #7, #8 */}
				{errorText && (
					<div className="inline-flex items-center justify-start gap-1.5 self-stretch">
						{/* Alert Circle Icon - Elemento #6 */}
						<div
							className="relative flex size-5 shrink-0 items-center justify-center"
							role="img"
							aria-label="Error"
						>
							<Icon
								name="question-mark-circled"
								size="sm"
								className="text-[var(--input-error-text)]"
								aria-hidden="true"
							/>
						</div>
						{/* Error Text - Elemento #8 */}
						<div
							id={`${finalId}-error`}
							className="relative shrink-0 font-['Open_Sans:Regular',_sans-serif] text-[14px] leading-[0] font-normal text-nowrap text-[var(--input-error-text)]"
							style={{ fontVariationSettings: "'wdth' 100" }}
						>
							<p className="leading-[20px] whitespace-pre">{errorText}</p>
						</div>
					</div>
				)}
			</div>
		)
	},
)

InputText.displayName = 'InputText'

export { InputText, inputTextVariants }
